// apps/admin-ui/src/components/SourceViewerDialog.tsx
// OTP-gated source code viewer for contract audit
// Re-authenticates before displaying contract source
// ALL RIGHTS RESERVED

import { useState, useEffect, useRef, useCallback } from 'react';
import { getCurrentEmployee } from '../utils/auth';
import * as authApi from '../api/auth';
import { getContractSource } from '../generated/contractSources';
import '../styles/source-viewer.css';

// =============================================================================
// TYPES
// =============================================================================

interface SourceViewerDialogProps {
  sourceFile: string;
  onClose: () => void;
}

type DialogStep = 'otp-request' | 'otp-verify' | 'source' | 'error';

// =============================================================================
// COMPONENT
// =============================================================================

export function SourceViewerDialog({ sourceFile, onClose }: SourceViewerDialogProps) {
  const employee = getCurrentEmployee();
  const [step, setStep] = useState<DialogStep>('otp-request');
  const [otp, setOtp] = useState('');
  const [source, setSource] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const otpRef = useRef<HTMLInputElement>(null);

  // Focus OTP input when it appears
  useEffect(() => {
    if (step === 'otp-verify' && otpRef.current) {
      otpRef.current.focus();
    }
  }, [step]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Step 1: Request OTP — sends code to employee's email
  const handleRequestOtp = useCallback(async () => {
    if (!employee?.email) return;
    setLoading(true);
    setError('');
    try {
      await authApi.employeeLogin(employee.email);
      setStep('otp-verify');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  }, [employee?.email]);

  // Step 2: Verify OTP then reveal source
  const handleVerifyOtp = useCallback(async () => {
    if (!employee?.email || otp.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      await authApi.employeeVerify(employee.email, otp);

      // OTP verified — read source from embedded build-time data
      const src = getContractSource(sourceFile);
      if (src) {
        setSource(src);
        setStep('source');
      } else {
        setError(`Source not found for: ${sourceFile}`);
        setStep('error');
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed');
      setOtp('');
      if (otpRef.current) otpRef.current.focus();
    } finally {
      setLoading(false);
    }
  }, [employee?.email, otp, sourceFile]);

  // OTP input handler
  const handleOtpChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 6);
    setOtp(cleaned);
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && otp.length === 6) {
      handleVerifyOtp();
    }
  };

  // Copy source to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(source);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // File name from path
  const fileName = sourceFile.split('/').pop() || sourceFile;

  return (
    <div className="sv-overlay" onClick={onClose}>
      <div
        className={`sv-dialog ${step === 'source' ? 'sv-dialog-wide' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        {/* ─── Header ─── */}
        <header className="sv-header">
          <div>
            <h2 className="sv-title">
              {step === 'source' ? fileName : 'Source Code Access'}
            </h2>
            <p className="sv-subtitle">
              {step === 'source'
                ? sourceFile
                : 'Re-authentication required to view contract source'}
            </p>
          </div>
          <button className="sv-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        {/* ─── Body ─── */}
        <div className="sv-body">
          {/* OTP Request Step */}
          {step === 'otp-request' && (
            <div className="sv-auth-step">
              <div className="sv-lock-icon">🔒</div>
              <p className="sv-auth-message">
                Viewing contract source code requires step-up authentication.
                A verification code will be sent to <strong>{employee?.email}</strong>.
              </p>
              <button
                className="sv-btn sv-btn-primary"
                onClick={handleRequestOtp}
                disabled={loading}
              >
                {loading ? 'Sending…' : 'Send Verification Code'}
              </button>
            </div>
          )}

          {/* OTP Verify Step */}
          {step === 'otp-verify' && (
            <div className="sv-auth-step">
              <p className="sv-auth-message">
                Enter the 6-digit code sent to <strong>{employee?.email}</strong>
              </p>
              <div className="sv-otp-input-group">
                <input
                  ref={otpRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="sv-otp-input"
                  value={otp}
                  onChange={e => handleOtpChange(e.target.value)}
                  onKeyDown={handleOtpKeyDown}
                  placeholder="000000"
                  maxLength={6}
                />
              </div>
              <div className="sv-auth-actions">
                <button
                  className="sv-btn sv-btn-secondary"
                  onClick={handleRequestOtp}
                  disabled={loading}
                >
                  Resend
                </button>
                <button
                  className="sv-btn sv-btn-primary"
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? 'Verifying…' : 'Verify & View Source'}
                </button>
              </div>
            </div>
          )}

          {/* Source Code Step */}
          {step === 'source' && (
            <div className="sv-source-container">
              <div className="sv-source-toolbar">
                <span className="sv-source-language">TypeScript</span>
                <span className="sv-source-meta">
                  {source.split('\n').length} lines
                </span>
                <button
                  className="sv-btn sv-btn-sm"
                  onClick={handleCopy}
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <pre className="sv-source-code">
                <code>
                  {source.split('\n').map((line, i) => (
                    <div key={i} className="sv-source-line">
                      <span className="sv-line-number">{i + 1}</span>
                      <span className="sv-line-content">{line}</span>
                    </div>
                  ))}
                </code>
              </pre>
            </div>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="sv-auth-step">
              <p className="sv-auth-message">
                Could not load source for <code>{sourceFile}</code>.
                Run <code>./apps/admin-ui/scripts/sync-contract-sources.sh</code> to regenerate.
              </p>
            </div>
          )}

          {/* Error banner (shows on auth failures without leaving current step) */}
          {error && step !== 'error' && (
            <div className="sv-error">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SourceViewerDialog;
